import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Tooltip } from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import Cropper from 'react-easy-crop';
import ApiClient from '../BaseUrl/ApiClient';

import "../../../src/Components/Common/crop.css";
import { getDLDealCroppedImg } from "../../../src/Components/Common/DealCropDL";

const mapContainerStyle = { height: '400px', width: '100%' };
const DEFAULT_COORDINATES = { lat: 43.6511, lng: -79.3470 };

function UpdateResturant() {
    const history = useHistory();
    const { id } = useParams();
    const businessId = id;

    const [googleTooltipOpen, setGoogleTooltipOpen] = useState(false);
    const toggleGoogleTooltip = () => setGoogleTooltipOpen(!googleTooltipOpen);

    const [formData, setFormData] = useState({
        businessName: '',
        telegramLink: '',
        googleBusinessLink: '',
        lunchSpecialStartTime: '',
        lunchSpecialEndTime: '',
        dailySpecialStartTime: '',
        dailySpecialEndTime: '',
        longitude: null,
        latitude: null,
        type: 'Point',
        postalCode: '',
        city: '',
        state: '',
        streetAddress: '',
        location: '',
        removeLogo: false
    });

    const [selectedPosition, setSelectedPosition] = useState(null);
    const [mapCenter, setMapCenter] = useState(DEFAULT_COORDINATES);

    // Logo and Cropper States
    const [logoPhoto, setLogoPhoto] = useState(null);
    const [existingLogo, setExistingLogo] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState('');
    const fileInputRef = useRef(null);

    const [showCropper, setShowCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [logoPhotoPreview, setLogoPhotoPreview] = useState(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    });

    /* ===================== FETCH BUSINESS DETAILS ===================== */
    useEffect(() => {
        if (!businessId) return;

        const fetchBusinessDetail = async () => {
            try {
                const res = await ApiClient().get(`/business/admin/${businessId}`);
                const business = res.data?.responseData?.business;
                if (!business) return;

                const coords = business.businessLocation?.coordinates || [];
                const longitude = coords.length >= 1 ? coords[0] : null;
                const latitude = coords.length >= 2 ? coords[1] : null;

                setFormData({
                    businessName: business.businessName || '',
                    telegramLink: business.telegramLink || '',
                    googleBusinessLink: business.googleBusinessLink || '',
                    lunchSpecialStartTime: business.lunchSpecialStartTime || '',
                    lunchSpecialEndTime: business.lunchSpecialEndTime || '',
                    dailySpecialStartTime: business.dailySpecialStartTime || '',
                    dailySpecialEndTime: business.dailySpecialEndTime || '',
                    longitude,
                    latitude,
                    type: business.businessLocation?.type || 'Point',
                    postalCode: business.businessLocation?.postalCode || '',
                    city: business.businessLocation?.city || '',
                    state: business.businessLocation?.state || '',
                    streetAddress: business.businessLocation?.streetAddress || '',
                    location: `${business.businessLocation?.streetAddress ? business.businessLocation.streetAddress + ', ' : ''}${business.businessLocation?.city || ''}${business.businessLocation?.city ? ', ' : ''}${business.businessLocation?.state || ''}${business.businessLocation?.state ? ', ' : ''}${business.businessLocation?.postalCode || ''}`,
                    removeLogo: false
                });

                if (latitude && longitude) {
                    setSelectedPosition({ lat: latitude, lng: longitude });
                    setMapCenter({ lat: latitude, lng: longitude });
                }

                if (business.logoImage?.url) {
                    setExistingLogo(business.logoImage.url);
                    setLogoPhotoPreview(business.logoImage.url);
                }
            } catch (error) {
                console.error('Failed to fetch business details:', error);
                toast.error('Failed to load business details');
            }
        };

        fetchBusinessDetail();
    }, [businessId]);

    /* ===================== MAP HANDLERS ===================== */
    const handleMapClick = useCallback((event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        setSelectedPosition({ lat, lng });
        setMapCenter({ lat, lng });
        updateAddressFromCoordinates(lat, lng);
    }, []);

    const handleMarkerDragEnd = (e) => handleMapClick(e);

    const extractStreetAddress = (formattedAddress, components) => {
        const get = (type, field) => components.find(c => c.types.includes(type))?.[field] || '';
        const removeParts = [
            get('locality', 'long_name'),
            get('locality', 'short_name'),
            get('administrative_area_level_2', 'long_name'),
            get('administrative_area_level_2', 'short_name'),
            get('administrative_area_level_1', 'long_name'),
            get('administrative_area_level_1', 'short_name'),
            get('postal_code', 'long_name'),
            get('country', 'long_name'),
            get('country', 'short_name')
        ].filter(Boolean);

        return formattedAddress
            .split(',')
            .map(part => part.trim())
            .filter(part => {
                let cleaned = part;
                removeParts.forEach(rp => {
                    cleaned = cleaned.replace(new RegExp(rp.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim();
                });
                return cleaned.length > 0;
            })
            .join(', ');
    };

    const updateAddressFromCoordinates = (lat, lng) => {
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const components = results[0].address_components;
                const getComp = type => components.find(c => c.types.includes(type))?.long_name || '';
                setFormData(prev => ({
                    ...prev,
                    postalCode: getComp('postal_code'),
                    city: getComp('locality') || getComp('administrative_area_level_2'),
                    state: getComp('administrative_area_level_1'),
                    streetAddress: extractStreetAddress(results[0].formatted_address, components),
                    location: results[0].formatted_address
                }));
            }
        });
    };

    const handleLocationBlur = () => {
        if (!formData.location) return;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: formData.location }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const loc = results[0].geometry.location;
                const lat = loc.lat();
                const lng = loc.lng();

                setSelectedPosition({ lat, lng });
                setMapCenter({ lat, lng });
                updateAddressFromCoordinates(lat, lng);
            } else {
                // toast.error('Location not found on map');
            }
        });
    };

    // Geocode location and return coordinates as a promise
    const geocodeLocation = (address) => {
        return new Promise((resolve, reject) => {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const loc = results[0].geometry.location;
                    const lat = loc.lat();
                    const lng = loc.lng();
                    const components = results[0].address_components;
                    const getComp = type => components.find(c => c.types.includes(type))?.long_name || '';
                    resolve({
                        latitude: lat,
                        longitude: lng,
                        postalCode: getComp('postal_code'),
                        city: getComp('locality') || getComp('administrative_area_level_2'),
                        state: getComp('administrative_area_level_1'),
                        streetAddress: extractStreetAddress(results[0].formatted_address, components),
                        location: results[0].formatted_address
                    });
                } else {
                    reject(new Error('Location not found'));
                }
            });
        });
    };

    /* ===================== INPUT CHANGE ===================== */
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'location' && value.trim() === '') {
            setSelectedPosition(null);
            setMapCenter(DEFAULT_COORDINATES);
            setFormData(prev => ({
                ...prev,
                location: '',
                latitude: null,
                longitude: null,
                postalCode: '',
                city: '',
                state: '',
                streetAddress: ''
            }));
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /* ===================== FILE & CROPPER ===================== */
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setSelectedFileName(file.name);
        const reader = new FileReader();
        reader.onload = () => {
            setImageToCrop(reader.result);
            setShowCropper(true);
            e.target.value = "";
        };
        reader.readAsDataURL(file);
    };

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleCropSave = async () => {
        try {
            const blob = await getDLDealCroppedImg(imageToCrop, croppedAreaPixels);
            const file = new File([blob], selectedFileName || 'logo.jpg', { type: 'image/jpeg' });
            setLogoPhoto(file);
            setLogoPhotoPreview(URL.createObjectURL(blob));
            setShowCropper(false);
            setExistingLogo(null); // remove old image
            setFormData(prev => ({ ...prev, removeLogo: false }));
        } catch {
            toast.error('Image crop failed');
        }
    };

    const handleRemoveLogo = () => {
        setLogoPhoto(null);
        setLogoPhotoPreview(null);
        setExistingLogo(null);
        setSelectedFileName('');
        setFormData(prev => ({ ...prev, removeLogo: true }));
    };

    /* ===================== SUBMIT ===================== */
    const handleSubmit = async (e) => {
        e.preventDefault();

        // if (!formData.businessName) {
        //     toast.error('Business Name is required');
        //     return;
        // }
        // if (!formData.latitude || !formData.longitude) {
        //     toast.error('Business Location is required');
        //     return;
        // }

        try {
            let submitData = { ...formData };

            // If location is provided but lat/lng are missing, geocode the location first
            if (submitData.location && (!submitData.latitude || !submitData.longitude)) {
                try {
                    const geoData = await geocodeLocation(submitData.location);
                    submitData = { ...submitData, ...geoData };
                    // Also update the form state and map
                    setFormData(prev => ({ ...prev, ...geoData }));
                    setSelectedPosition({ lat: geoData.latitude, lng: geoData.longitude });
                    setMapCenter({ lat: geoData.latitude, lng: geoData.longitude });
                } catch (geoErr) {
                    toast.error('Could not find coordinates for the entered location. Please select from map or enter a valid address.');
                    return;
                }
            }

            const data = new FormData();

            Object.entries(submitData).forEach(([key, value]) => {
                if (value !== null && value !== '') data.append(key, value);
            });

            if (logoPhoto) data.append('logoPhoto', logoPhoto);

            await ApiClient().put(`/business/admin/update/${businessId}`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Business profile updated successfully');
            history.push('/business-list');
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.responseMessage?.[0] || 'Failed to update business profile');
        }
    };

    if (!isLoaded) return <div>Loading Google Maps...</div>;

    return (
        <Container className="mt-4 mb-4">
            <ToastContainer />
            <h3>Update Business Profile</h3>

            <Form onSubmit={handleSubmit} encType="multipart/form-data">

                {/* Business Name */}
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Business Name *</Label>
                            <Input type="text" name="businessName" value={formData.businessName} onChange={handleInputChange} required />
                        </FormGroup>
                    </Col>

                    {/* Logo Photo with Cropper */}
                    <Col md={6}>
                        <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Logo Photo</Label>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="text"
                                    value={selectedFileName || ''}
                                    placeholder="No file chosen"
                                    readOnly
                                    style={{ width: '250px' }}
                                />
                                <Button type="button" color="primary" onClick={() => fileInputRef.current.click()}>Choose File</Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </div>

                            {(logoPhotoPreview || existingLogo) && (
                                <div style={{ position: 'relative', marginTop: 10, display: 'inline-block' }}>
                                    <img
                                        src={logoPhotoPreview || existingLogo}
                                        alt="Logo Preview"
                                        style={{ maxHeight: 100, border: '1px solid #ccc', display: 'block' }}
                                    />
                                    <span
                                        onClick={handleRemoveLogo}
                                        style={{
                                            position: 'absolute',
                                            top: -5,
                                            right: -5,
                                            background: '#ff3c3fff',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 20,
                                            height: 20,
                                            textAlign: 'center',
                                            lineHeight: '20px',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            fontSize: 14
                                        }}
                                    >
                                        ×
                                    </span>
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                </Row>

                {/* Cropper Modal */}
                {showCropper && (
                    <div className="crop-modal">
                        <div className="crop-modal-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4>Crop Logo</h4>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => setShowCropper(false)}>&times;</button>
                            </div>
                            <div className="crop-container">
                                <Cropper
                                    image={imageToCrop}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1} // square crop for logo
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                />
                            </div>
                            <div className="crop-controls my-2">
                                <label>Zoom</label>
                                <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
                            </div>
                            <div className="crop-buttons mt-2">
                                <Button color="secondary" onClick={() => setShowCropper(false)}>Cancel</Button>{' '}
                                <Button color="primary" onClick={handleCropSave}>Save Crop</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Location */}
                <FormGroup>
                    <Label>Location *</Label>
                    <Input type="text" name="location" value={formData.location} onChange={handleInputChange} onBlur={handleLocationBlur} placeholder="Enter address or click on map" />
                </FormGroup>

                <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13} onClick={handleMapClick}>
                    {selectedPosition && <Marker position={selectedPosition} draggable onDragEnd={handleMarkerDragEnd} />}
                </GoogleMap>

                {/* Other Fields */}
                <Row className="mt-3">
                    <Col md={6}>
                        <FormGroup>
                            <Label>Google Link <span id="googleBusinessInfo" style={{ cursor: 'pointer', marginLeft: 6, color: '#0d6efd', fontWeight: 'bold' }}>ⓘ</span></Label>
                            <Input name="googleBusinessLink" value={formData.googleBusinessLink} onChange={handleInputChange} />
                            <Tooltip placement="right" isOpen={googleTooltipOpen} target="googleBusinessInfo" toggle={toggleGoogleTooltip}>
                                Click for hours, phone number, or more info
                            </Tooltip>
                        </FormGroup>
                    </Col>

                    <Col md={6}>
                        <FormGroup>
                            <Label>Lunch Special Time</Label>
                            <div className="d-flex gap-2">
                                <Input
                                    type="time"
                                    name="lunchSpecialStartTime"
                                    value={formData.lunchSpecialStartTime || ''}
                                    onChange={handleInputChange}
                                />
                                <span className="align-self-center">to</span>
                                <Input
                                    type="time"
                                    name="lunchSpecialEndTime"
                                    value={formData.lunchSpecialEndTime || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </FormGroup>
                    </Col>
                </Row>

            

                <Button color="primary" type="submit">Update Business Profile</Button>
            </Form>
        </Container>
    );
}

export default UpdateResturant;
